﻿import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { FileText, CheckSquare, CheckCircle, LogOut, Menu, X, BarChart2, Home } from 'lucide-react';

export const Sidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logoutVoter } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [allowLiveResults, setAllowLiveResults] = useState(() => {
    const settings = api.getElectionSettingsSync();
    return settings.allowLiveResults !== false;
  });

  useEffect(() => {
    api.getElectionInfo().then((info) => {
      if (info) setAllowLiveResults(info.allowLiveResults !== false);
    });
  }, []);

  const handleLogout = () => {
    logoutVoter();
    navigate('/');
  };

  const navItems = [
    { label: 'Check Details', path: '/vote', icon: FileText },
    { label: 'Cast Vote', path: '/vote/cast-vote', icon: CheckSquare },
    { label: 'Verify Choices', path: '/vote/verify', icon: CheckCircle },
    ...(allowLiveResults ? [{ label: 'Live Results', path: '/results', icon: BarChart2 }] : []),
  ];

  return (
    <>
      {/* Desktop Sidebar (Fixed Left, 380px, #171619) */}
      <aside className="dashboard-sidebar-desktop">
        <div>
          <div style={{ marginBottom: '50px' }}>
            <Link to="/">
              <img
                src="/assets/images/electpoll logo (plain white).png"
                alt="Electpoll"
                style={{ height: '44px', display: 'block' }}
              />
            </Link>
          </div>

          <nav style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    padding: '14px 20px',
                    borderRadius: '8px',
                    backgroundColor: isActive ? '#FF9900' : 'transparent',
                    color: isActive ? '#171619' : '#FFFFFF',
                    fontWeight: 600,
                    fontSize: '15px',
                    transition: 'all 0.25s ease',
                  }}
                >
                  <Icon size={20} color={isActive ? '#171619' : '#FF9900'} />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>
        </div>

        <div>
          <button
            onClick={handleLogout}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              padding: '14px 20px',
              borderRadius: '8px',
              backgroundColor: 'transparent',
              border: 'none',
              color: '#EF4444',
              fontWeight: 600,
              fontSize: '15px',
              cursor: 'pointer',
              width: '100%',
              textAlign: 'left',
            }}
          >
            <LogOut size={20} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Legacy-Style Mobile Header (Light Background #F4F4F4, Main Logo, Dark Menu Button) */}
      <header className="dashboard-header-mobile">
        <Link to="/" style={{ display: 'flex', alignItems: 'center' }}>
          <img
            src="/assets/images/electpoll logo.png"
            alt="Electpoll"
            style={{ height: '36px' }}
          />
        </Link>
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle navigation menu"
          style={{
            background: 'none',
            border: 'none',
            color: '#171619',
            cursor: 'pointer',
            padding: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {mobileMenuOpen ? <X size={28} color="#171619" /> : <Menu size={28} color="#171619" />}
        </button>
      </header>

      {/* Legacy-Style Backdrop Overlay */}
      {mobileMenuOpen && (
        <div
          onClick={() => setMobileMenuOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.45)',
            backdropFilter: 'blur(2px)',
            zIndex: 998,
          }}
        />
      )}

      {/* Legacy-Style Slide-In Right Menu Drawer */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: mobileMenuOpen ? 0 : '-360px',
          width: '320px',
          maxWidth: '82vw',
          height: '100%',
          backgroundColor: '#171619',
          color: '#FFFFFF',
          padding: '24px 20px',
          zIndex: 999,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          boxSizing: 'border-box',
          boxShadow: mobileMenuOpen ? '-4px 0 24px rgba(0,0,0,0.35)' : 'none',
          transition: 'right 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          overflowY: 'auto',
        }}
      >
        <div>
          {/* Drawer Header with Plain White Logo and Close X */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '32px',
              paddingBottom: '16px',
              borderBottom: '1px solid #2D2C31',
            }}
          >
            <img
              src="/assets/images/electpoll logo (plain white).png"
              alt="Electpoll"
              style={{ height: '30px' }}
            />
            <button
              onClick={() => setMobileMenuOpen(false)}
              aria-label="Close menu"
              style={{
                background: 'none',
                border: 'none',
                color: '#A1A1AA',
                cursor: 'pointer',
                padding: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <X size={24} />
            </button>
          </div>

          {/* Legacy Navigation Links */}
          <nav style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '14px',
                padding: '12px 16px',
                borderRadius: '8px',
                color: '#D1D5DB',
                fontWeight: 600,
                fontSize: '15px',
                transition: 'all 0.2s ease',
              }}
            >
              <Home size={18} color="#FF9900" />
              <span>Home</span>
            </Link>

            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '14px',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    backgroundColor: isActive ? '#FF9900' : 'transparent',
                    color: isActive ? '#171619' : '#FFFFFF',
                    fontWeight: 600,
                    fontSize: '15px',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <Icon size={18} color={isActive ? '#171619' : '#FF9900'} />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>
        </div>

        {/* Drawer Footer / Logout */}
        <div style={{ paddingTop: '20px', borderTop: '1px solid #2D2C31', marginTop: '20px' }}>
          <button
            onClick={() => {
              setMobileMenuOpen(false);
              handleLogout();
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px 16px',
              color: '#EF4444',
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.2)',
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '14px',
              cursor: 'pointer',
              width: '100%',
              justifyContent: 'center',
            }}
          >
            <LogOut size={18} />
            <span>Logout</span>
          </button>
        </div>
      </div>
    </>
  );
};
